// src/components/reports/ReportForm.js - Human rights 19-field report form (Chayyek)
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import FileUpload from './FileUpload';
import api from '../../utils/api';
import { getErrorMessage } from '../../utils/error';
import countries from 'world-countries';
import { analyticsEvents } from '../../utils/analyticsEvents';

const VIOLATION_KEYS = ['targeting_civilians', 'forced_displacement', 'property_destruction', 'deprivation_housing_aid', 'arbitrary_arrest', 'hate_speech_incitement', 'other'];
const AFFECTED_TYPE_KEYS = ['individuals', 'family', 'group', 'community'];
const AFFECTED_CATEGORY_KEYS = ['women', 'children', 'elderly', 'civilians', 'other'];
const INFO_SOURCE_KEYS = ['eyewitness', 'video', 'photos', 'social_media', 'local_org', 'media', 'other'];
const SAFETY_RISK_KEYS = ['witness_risk', 'sensitive_info', 'internal_use_only'];
const LEGAL_KEYS = ['human_rights_violation', 'potential_war_crime', 'needs_legal_review'];

function parseEvidenceLinksInitial(value) {
  if (!value) return [{ label: '', url: '' }];
  if (Array.isArray(value)) {
    const list = value
      .map((item) => ({
        label: typeof item?.label === 'string' ? item.label : '',
        url: typeof item?.url === 'string' ? item.url : ''
      }))
      .filter((e) => e.url.trim());
    return list.length ? list : [{ label: '', url: '' }];
  }
  if (typeof value !== 'string' || !value.trim()) return [{ label: '', url: '' }];
  const lines = value.trim().split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  const urlRe = /(https?:\/\/[^\s]+)/i;
  const out = [];
  for (const line of lines) {
    const match = line.match(urlRe);
    if (match) out.push({ label: line.slice(0, match.index).trim(), url: match[1].trim() });
  }
  return out.length ? out : [{ label: '', url: '' }];
}

const ReportForm = ({ onSuccess, onCancel, initialData, reportId, editMode }) => {
  const isEdit = editMode || (initialData && reportId);
  const { t } = useTranslation();
  const [caseNumber, setCaseNumber] = useState('');
  const [monitoringDate, setMonitoringDate] = useState('');
  const [reporterName, setReporterName] = useState('');
  const [violationType, setViolationType] = useState([]);
  const [violationTypeOther, setViolationTypeOther] = useState('');
  const [description, setDescription] = useState('');
  const [incidentDate, setIncidentDate] = useState('');
  const [incidentTime, setIncidentTime] = useState('');
  const [locationPlace, setLocationPlace] = useState('');
  const [locationCountry, setLocationCountry] = useState('');
  const [locationRegion, setLocationRegion] = useState('');
  const [affectedType, setAffectedType] = useState([]);
  const [affectedCategory, setAffectedCategory] = useState([]);
  const [affectedCategoryOther, setAffectedCategoryOther] = useState('');
  const [affectedCount, setAffectedCount] = useState('');
  const [affectedCountKnown, setAffectedCountKnown] = useState('');
  const [responsibleParty, setResponsibleParty] = useState('');
  const [responsiblePartyNotes, setResponsiblePartyNotes] = useState('');
  const [infoSources, setInfoSources] = useState([]);
  const [infoSourcesOther, setInfoSourcesOther] = useState('');
  const [evidenceLinksList, setEvidenceLinksList] = useState([{ label: '', url: '' }]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [initialVerification, setInitialVerification] = useState('');
  const [verificationNotes, setVerificationNotes] = useState('');
  const [reliabilityLevel, setReliabilityLevel] = useState('');
  const [safetyRisks, setSafetyRisks] = useState([]);
  const [legalClassification, setLegalClassification] = useState([]);
  const [reporterNotes, setReporterNotes] = useState('');
  const [status, setStatus] = useState('pending');
  const [attachmentUrlsToKeep, setAttachmentUrlsToKeep] = useState([]);
  const [attachmentFilenamesToKeep, setAttachmentFilenamesToKeep] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    if (!isEdit || !initialData) return;
    const report = initialData;
    const fd = report.form_data && typeof report.form_data === 'object' ? report.form_data : {};
    setDescription(report.input_data || '');
    setLocationCountry(report.location_country || '');
    setLocationRegion(report.location_region || '');
    setStatus(report.status || 'pending');
    setCaseNumber(fd.case_number || '');
    setMonitoringDate(fd.monitoring_date || '');
    setReporterName(fd.reporter_name || '');
    const vt = Array.isArray(fd.violation_type) ? fd.violation_type : [];
    const vtEnum = vt.filter(v => VIOLATION_KEYS.includes(v));
    const vtOther = vt.find(v => typeof v === 'string' && v.trim() && !VIOLATION_KEYS.includes(v));
    setViolationType(vtOther ? [...vtEnum, 'other'] : vtEnum);
    setViolationTypeOther(vtOther && typeof vtOther === 'string' ? vtOther.trim() : '');
    setIncidentDate(fd.incident_date || '');
    setIncidentTime(fd.incident_time || '');
    setLocationPlace(fd.location_place || '');
    setAffectedType(Array.isArray(fd.affected_type) ? fd.affected_type : []);
    const ac = Array.isArray(fd.affected_category) ? fd.affected_category : [];
    const acEnum = ac.filter(x => AFFECTED_CATEGORY_KEYS.includes(x));
    const acOther = ac.find(x => typeof x === 'string' && x.trim() && !AFFECTED_CATEGORY_KEYS.includes(x));
    setAffectedCategory(acOther ? [...acEnum, 'other'] : acEnum);
    setAffectedCategoryOther(acOther && typeof acOther === 'string' ? acOther.trim() : '');
    setAffectedCount(fd.affected_count !== undefined && fd.affected_count !== null ? String(fd.affected_count) : '');
    setAffectedCountKnown(fd.affected_count_known || '');
    setResponsibleParty(fd.responsible_party || '');
    setResponsiblePartyNotes(fd.responsible_party_notes || '');
    const isrc = Array.isArray(fd.info_sources) ? fd.info_sources : [];
    const isrcEnum = isrc.filter(x => INFO_SOURCE_KEYS.includes(x));
    const isrcOther = isrc.find(x => typeof x === 'string' && x.trim() && !INFO_SOURCE_KEYS.includes(x));
    setInfoSources(isrcOther ? [...isrcEnum, 'other'] : isrcEnum);
    setInfoSourcesOther(isrcOther && typeof isrcOther === 'string' ? isrcOther.trim() : '');
    setEvidenceLinksList(parseEvidenceLinksInitial(fd.evidence_links));
    setInitialVerification(fd.initial_verification || '');
    setVerificationNotes(fd.verification_notes || '');
    setReliabilityLevel(fd.reliability_level || '');
    setSafetyRisks(Array.isArray(fd.safety_risks) ? fd.safety_risks : []);
    setLegalClassification(Array.isArray(fd.legal_classification) ? fd.legal_classification : []);
    setReporterNotes(fd.reporter_notes || '');
    const urls = report.attachment_urls || [];
    setAttachmentUrlsToKeep(urls);
    const fnames = fd.attachment_filenames || [];
    setAttachmentFilenamesToKeep(Array.isArray(fnames) && fnames.length === urls.length ? fnames : urls.map(() => ''));
  }, [isEdit, initialData]);

  const countryOptions = useMemo(() => {
    return countries
      .map(c => ({ value: c.name.common, label: c.name.common, flag: c.flag }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const formatOptionLabel = ({ label, flag }) => (
    <div className="flex items-center gap-2">
      <span className="text-xl">{flag}</span>
      <span>{label}</span>
    </div>
  );

  const isDarkMode = document.documentElement.classList.contains('dark');
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: isDarkMode ? '#2A2A2A' : 'white',
      borderColor: state.isFocused ? '#751F19' : (isDarkMode ? '#333333' : '#E0E0E0'),
      color: isDarkMode ? '#FFFFFF' : '#1A1A1A',
      minHeight: '48px',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#751F19' : state.isFocused ? (isDarkMode ? '#333333' : '#F3F4F6') : (isDarkMode ? '#2A2A2A' : 'white'),
      color: state.isSelected ? 'white' : (isDarkMode ? '#E0E0E0' : '#1A1A1A'),
      cursor: 'pointer',
    }),
    menu: (base) => ({ ...base, backgroundColor: isDarkMode ? '#2A2A2A' : 'white', zIndex: 9999 }),
    singleValue: (base) => ({ ...base, color: isDarkMode ? '#FFFFFF' : '#1A1A1A' }),
    input: (base) => ({ ...base, color: isDarkMode ? '#FFFFFF' : '#1A1A1A' }),
    placeholder: (base) => ({ ...base, color: isDarkMode ? '#8A8A8A' : '#6A6A6A' }),
    menuList: (base) => ({ ...base, backgroundColor: isDarkMode ? '#2A2A2A' : 'white' }),
  };

  const toggleArray = (arr, key) => {
    if (arr.includes(key)) return arr.filter(k => k !== key);
    return [...arr, key];
  };

  const buildPayload = () => {
    const violationArr = [...violationType];
    if (violationType.includes('other') && violationTypeOther.trim()) violationArr.push(violationTypeOther.trim());
    const affectedCategoryArr = [...affectedCategory];
    if (affectedCategory.includes('other') && affectedCategoryOther.trim()) affectedCategoryArr.push(affectedCategoryOther.trim());
    const infoSourcesArr = [...infoSources];
    if (infoSources.includes('other') && infoSourcesOther.trim()) infoSourcesArr.push(infoSourcesOther.trim());
    const payload = {
      description: description.trim(),
      status: isEdit ? status : undefined,
      case_number: caseNumber.trim() || undefined,
      monitoring_date: monitoringDate || undefined,
      reporter_name: reporterName.trim() || undefined,
      violation_type: violationArr.length ? violationArr : undefined,
      incident_date: incidentDate.trim() || undefined,
      incident_time: incidentTime.trim() || undefined,
      location_place: locationPlace.trim() || undefined,
      location_country: locationCountry || undefined,
      location_region: locationRegion.trim() || undefined,
      affected_type: affectedType.length ? affectedType : undefined,
      affected_category: affectedCategoryArr.length ? affectedCategoryArr : undefined,
      affected_count: affectedCount !== '' ? affectedCount : undefined,
      affected_count_known: affectedCountKnown || undefined,
      responsible_party: responsibleParty.trim() || undefined,
      responsible_party_notes: responsiblePartyNotes.trim() || undefined,
      info_sources: infoSourcesArr.length ? infoSourcesArr : undefined,
      evidence_links: evidenceLinksList.filter((e) => e.url.trim()).length
        ? evidenceLinksList.filter((e) => e.url.trim()).map((e) => ({ label: e.label.trim() || null, url: e.url.trim() }))
        : undefined,
      initial_verification: initialVerification || undefined,
      verification_notes: verificationNotes.trim() || undefined,
      reliability_level: reliabilityLevel || undefined,
      safety_risks: safetyRisks.length ? safetyRisks : undefined,
      legal_classification: legalClassification.length ? legalClassification : undefined,
      reporter_notes: reporterNotes.trim() || undefined,
    };
    if (isEdit) {
      payload.attachment_urls = attachmentUrlsToKeep;
      payload.attachment_filenames = attachmentFilenamesToKeep;
    }
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    analyticsEvents.reportSubmitAttempt(isEdit ? 'editor_update' : 'anonymous');
    if (!description || description.trim().length < 10) {
      setError(t('report.validation.textRequired') || 'Description is required (at least 10 characters).');
      setLoading(false);
      analyticsEvents.reportSubmitFailure(isEdit ? 'editor_update' : 'anonymous');
      return;
    }
    try {
      if (isEdit && reportId) {
        let urlsToSend = [...attachmentUrlsToKeep];
        let filenamesToSend = [...attachmentFilenamesToKeep];
        if (selectedFiles.length > 0) {
          const formData = new FormData();
          selectedFiles.forEach((file) => formData.append('files', file));
          const uploadRes = await api.post(`/api/reports/report/${reportId}/attachments`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000
          });
          if (uploadRes.data && uploadRes.data.urls && uploadRes.data.filenames) {
            urlsToSend = [...urlsToSend, ...uploadRes.data.urls];
            filenamesToSend = [...filenamesToSend, ...uploadRes.data.filenames];
          }
        }
        const payload = buildPayload();
        payload.attachment_urls = urlsToSend;
        payload.attachment_filenames = filenamesToSend;
        const response = await api.patch(`/api/reports/report/${reportId}`, payload, { timeout: 30000 });
        if (response.data && response.data.report) {
          analyticsEvents.reportSubmitSuccess('editor_update', selectedFiles.length > 0);
          if (onSuccess) onSuccess(response.data.report);
        }
      } else {
        const formData = new FormData();
        const p = buildPayload();
        formData.append('description', p.description);
        if (p.case_number) formData.append('case_number', p.case_number);
        if (p.monitoring_date) formData.append('monitoring_date', p.monitoring_date);
        if (p.reporter_name) formData.append('reporter_name', p.reporter_name);
        if (p.violation_type && p.violation_type.length) formData.append('violation_type', JSON.stringify(p.violation_type));
        if (p.incident_date) formData.append('incident_date', p.incident_date);
        if (p.incident_time) formData.append('incident_time', p.incident_time);
        if (p.location_place) formData.append('location_place', p.location_place);
        if (p.location_country) formData.append('location_country', p.location_country);
        if (p.location_region) formData.append('location_region', p.location_region);
        if (p.affected_type && p.affected_type.length) formData.append('affected_type', JSON.stringify(p.affected_type));
        if (p.affected_category && p.affected_category.length) formData.append('affected_category', JSON.stringify(p.affected_category));
        if (p.affected_count !== undefined) formData.append('affected_count', p.affected_count);
        if (p.affected_count_known) formData.append('affected_count_known', p.affected_count_known);
        if (p.responsible_party) formData.append('responsible_party', p.responsible_party);
        if (p.responsible_party_notes) formData.append('responsible_party_notes', p.responsible_party_notes);
        if (p.info_sources && p.info_sources.length) formData.append('info_sources', JSON.stringify(p.info_sources));
        if (p.evidence_links) formData.append('evidence_links', JSON.stringify(p.evidence_links));
        if (p.initial_verification) formData.append('initial_verification', p.initial_verification);
        if (p.verification_notes) formData.append('verification_notes', p.verification_notes);
        if (p.reliability_level) formData.append('reliability_level', p.reliability_level);
        if (p.safety_risks && p.safety_risks.length) formData.append('safety_risks', JSON.stringify(p.safety_risks));
        if (p.legal_classification && p.legal_classification.length) formData.append('legal_classification', JSON.stringify(p.legal_classification));
        if (p.reporter_notes) formData.append('reporter_notes', p.reporter_notes);
        selectedFiles.forEach((file) => formData.append('files', file));

        const response = await api.post('/api/reports/anonymous', formData, { timeout: 60000 });
        if (response.data) {
          analyticsEvents.reportSubmitSuccess('anonymous', selectedFiles.length > 0);
          setSuccess(true);
          if (onSuccess) onSuccess(response.data.report);
        }
      }
    } catch (err) {
      analyticsEvents.reportSubmitFailure(isEdit ? 'editor_update' : 'anonymous');
      setError(getErrorMessage(err, isEdit ? 'Failed to update report' : 'Failed to submit report'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSuccess(false);
    setCaseNumber('');
    setMonitoringDate('');
    setReporterName('');
    setViolationType([]);
    setViolationTypeOther('');
    setDescription('');
    setIncidentDate('');
    setIncidentTime('');
    setLocationPlace('');
    setLocationCountry('');
    setLocationRegion('');
    setAffectedType([]);
    setAffectedCategory([]);
    setAffectedCategoryOther('');
    setInfoSources([]);
    setInfoSourcesOther('');
    setAffectedCount('');
    setAffectedCountKnown('');
    setResponsibleParty('');
    setResponsiblePartyNotes('');
    setInfoSources([]);
    setEvidenceLinksList([{ label: '', url: '' }]);
    setSelectedFiles([]);
    setFileError('');
    setInitialVerification('');
    setVerificationNotes('');
    setReliabilityLevel('');
    setSafetyRisks([]);
    setLegalClassification([]);
    setReporterNotes('');
    setError('');
  };

  if (success) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold mb-2 text-shayek-light-900 dark:text-shayek-dark-900">{t('report.successTitle')}</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">{t('report.successMessage')}</p>
        <button onClick={handleReset} className="px-6 py-2 bg-shayek-primary dark:bg-shayek-red-light text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark transition-colors">
          {t('report.submitAnother')}
        </button>
      </div>
    );
  }

  const inputClass = 'w-full p-3 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900';
  const labelClass = 'block text-sm font-medium mb-2 text-shayek-light-700 dark:text-shayek-dark-700';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {isEdit && (
        <div>
          <label className={labelClass}>{t('journalist.reports.filters.status')}</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={`${inputClass} max-w-xs`}
          >
            <option value="pending">{t('journalist.reports.status.pending')}</option>
            <option value="pending_review">{t('journalist.reports.status.pending_review')}</option>
            <option value="verified">{t('journalist.reports.status.verified')}</option>
            <option value="rejected">{t('journalist.reports.status.rejected')}</option>
            <option value="flagged">{t('journalist.reports.status.flagged')}</option>
          </select>
        </div>
      )}
      {/* Instructions */}
      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
        <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">{t('report.sections.caseInfo')} – {t('common.optional')}</h3>
        <ul className="list-disc list-inside text-sm text-amber-800 dark:text-amber-300 space-y-1">
          {[1, 2, 3, 4].map(i => (
            <li key={i}>{t(`report.instructions.${i}`)}</li>
          ))}
        </ul>
      </div>

      {/* Case info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>{t('report.caseNumber')}</label>
          <input type="text" value={caseNumber} onChange={e => setCaseNumber(e.target.value)} className={inputClass} placeholder={t('report.caseNumberPlaceholder')} maxLength={100} />
        </div>
        <div>
          <label className={labelClass}>{t('report.monitoringDate')}</label>
          <input type="date" value={monitoringDate} onChange={e => setMonitoringDate(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>{t('report.reporterName')}</label>
          <input type="text" value={reporterName} onChange={e => setReporterName(e.target.value)} className={inputClass} placeholder={t('report.reporterNamePlaceholder')} maxLength={255} />
        </div>
      </div>

      {/* Violation type */}
      <div>
        <label className={labelClass}>{t('report.violationType')}</label>
        <div className="flex flex-wrap gap-3">
          {VIOLATION_KEYS.map(key => (
            <label key={key} className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={violationType.includes(key)} onChange={() => setViolationType(toggleArray(violationType, key))} className="rounded border-shayek-light-300 text-shayek-primary focus:ring-shayek-primary" />
              <span className="text-sm text-shayek-light-900 dark:text-shayek-dark-900">{t(`report.violationTypeOptions.${key}`)}</span>
            </label>
          ))}
        </div>
        {violationType.includes('other') && (
          <input type="text" value={violationTypeOther} onChange={e => setViolationTypeOther(e.target.value)} className={`${inputClass} mt-2`} placeholder={t('report.violationTypeOtherPlaceholder')} maxLength={200} />
        )}
      </div>

      {/* Description (required) */}
      <div>
        <label className={labelClass}>{t('report.description')} *</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} className={`${inputClass} h-40`} placeholder={t('report.descriptionPlaceholder')} required minLength={10} maxLength={10000} />
        <p className="mt-1 text-xs text-gray-500 dark:text-shayek-dark-500">{description.length}/10000</p>
      </div>

      {/* Incident date/time */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>{t('report.incidentDate')}</label>
          <input type="text" value={incidentDate} onChange={e => setIncidentDate(e.target.value)} className={inputClass} placeholder={t('report.incidentDatePlaceholder')} maxLength={100} />
        </div>
        <div>
          <label className={labelClass}>{t('report.incidentTime')}</label>
          <input type="text" value={incidentTime} onChange={e => setIncidentTime(e.target.value)} className={inputClass} placeholder={t('report.incidentTimePlaceholder')} maxLength={50} />
        </div>
      </div>

      {/* Location */}
      <div className="space-y-4">
        <div>
          <label className={labelClass}>{t('report.locationPlace')}</label>
          <input type="text" value={locationPlace} onChange={e => setLocationPlace(e.target.value)} className={inputClass} placeholder={t('report.locationPlacePlaceholder')} maxLength={500} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>{t('report.country')}</label>
            <Select value={countryOptions.find(o => o.value === locationCountry) || null} onChange={opt => setLocationCountry(opt ? opt.value : '')} options={countryOptions} formatOptionLabel={formatOptionLabel} isClearable isSearchable placeholder={t('report.selectCountry')} styles={selectStyles} />
          </div>
          <div>
            <label className={labelClass}>{t('report.region')}</label>
            <input type="text" value={locationRegion} onChange={e => setLocationRegion(e.target.value)} className={inputClass} placeholder={t('report.regionPlaceholder')} maxLength={100} />
          </div>
        </div>
      </div>

      {/* Affected */}
      <div>
        <label className={labelClass}>{t('report.affectedType')}</label>
        <div className="flex flex-wrap gap-3 mb-2">
          {AFFECTED_TYPE_KEYS.map(key => (
            <label key={key} className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={affectedType.includes(key)} onChange={() => setAffectedType(toggleArray(affectedType, key))} className="rounded border-shayek-light-300 text-shayek-primary focus:ring-shayek-primary" />
              <span className="text-sm">{t(`report.affectedTypeOptions.${key}`)}</span>
            </label>
          ))}
        </div>
        <label className={labelClass}>{t('report.affectedCategory')}</label>
        <div className="flex flex-wrap gap-3 mb-2">
          {AFFECTED_CATEGORY_KEYS.map(key => (
            <label key={key} className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={affectedCategory.includes(key)} onChange={() => setAffectedCategory(toggleArray(affectedCategory, key))} className="rounded border-shayek-light-300 text-shayek-primary focus:ring-shayek-primary" />
              <span className="text-sm">{t(`report.affectedCategoryOptions.${key}`)}</span>
            </label>
          ))}
        </div>
        {affectedCategory.includes('other') && (
          <input type="text" value={affectedCategoryOther} onChange={e => setAffectedCategoryOther(e.target.value)} className={`${inputClass} mt-2`} placeholder={t('report.affectedCategoryOtherPlaceholder')} maxLength={200} />
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>{t('report.affectedCount')}</label>
            <input type="number" min={0} value={affectedCount} onChange={e => setAffectedCount(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('report.affectedCountKnown')}</label>
            <select value={affectedCountKnown} onChange={e => setAffectedCountKnown(e.target.value)} className={inputClass}>
              <option value="">—</option>
              <option value="known">{t('report.affectedCountKnownOptions.known')}</option>
              <option value="suspected">{t('report.affectedCountKnownOptions.suspected')}</option>
              <option value="unknown">{t('report.affectedCountKnownOptions.unknown')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Responsible party */}
      <div className="space-y-4">
        <div>
          <label className={labelClass}>{t('report.responsibleParty')}</label>
          <input type="text" value={responsibleParty} onChange={e => setResponsibleParty(e.target.value)} className={inputClass} placeholder={t('report.responsiblePartyPlaceholder')} maxLength={500} />
        </div>
        <div>
          <label className={labelClass}>{t('report.responsiblePartyNotes')}</label>
          <textarea value={responsiblePartyNotes} onChange={e => setResponsiblePartyNotes(e.target.value)} className={`${inputClass} h-24`} maxLength={2000} />
        </div>
      </div>

      {/* Info sources */}
      <div>
        <label className={labelClass}>{t('report.infoSources')}</label>
        <div className="flex flex-wrap gap-3">
          {INFO_SOURCE_KEYS.map(key => (
            <label key={key} className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={infoSources.includes(key)} onChange={() => setInfoSources(toggleArray(infoSources, key))} className="rounded border-shayek-light-300 text-shayek-primary focus:ring-shayek-primary" />
              <span className="text-sm">{t(`report.infoSourcesOptions.${key}`)}</span>
            </label>
          ))}
        </div>
        {infoSources.includes('other') && (
          <input type="text" value={infoSourcesOther} onChange={e => setInfoSourcesOther(e.target.value)} className={`${inputClass} mt-2`} placeholder={t('report.infoSourcesOtherPlaceholder')} maxLength={200} />
        )}
      </div>

      {/* Evidence links and attachments */}
      <div className="space-y-4 p-4 border border-shayek-light-200 dark:border-shayek-dark-200 rounded-lg bg-shayek-light-50/50 dark:bg-shayek-dark-50/30">
        <h3 className="text-sm font-semibold text-shayek-light-800 dark:text-shayek-dark-800">{t('report.evidenceLinksAndAttachments', 'Evidence links and attachments')}</h3>
        <div>
          <label className={labelClass}>{t('report.evidenceLinks')}</label>
          <div className="space-y-2">
            {evidenceLinksList.map((entry, idx) => (
              <div key={idx} className="flex flex-wrap gap-2 items-start border border-shayek-light-200 dark:border-shayek-dark-200 rounded p-2 bg-white dark:bg-shayek-dark-50">
                <input
                  type="text"
                  value={entry.label}
                  onChange={(e) => setEvidenceLinksList((prev) => prev.map((item, i) => (i === idx ? { ...item, label: e.target.value } : item)))}
                  className={`${inputClass} flex-1 min-w-[120px]`}
                  placeholder={t('report.evidenceLinkLabelPlaceholder', 'Optional label')}
                  maxLength={500}
                />
                <input
                  type="url"
                  value={entry.url}
                  onChange={(e) => setEvidenceLinksList((prev) => prev.map((item, i) => (i === idx ? { ...item, url: e.target.value } : item)))}
                  className={`${inputClass} flex-1 min-w-[180px]`}
                  placeholder={t('report.evidenceLinksPlaceholder')}
                  maxLength={2000}
                />
                <button
                  type="button"
                  onClick={() => setEvidenceLinksList((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : [{ label: '', url: '' }]))}
                  className="px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors shrink-0"
                >
                  {t('common.remove')}
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setEvidenceLinksList((prev) => [...prev, { label: '', url: '' }])}
              className="text-sm text-shayek-primary dark:text-shayek-red-light hover:underline"
            >
              {t('report.addEvidenceLink', 'Add link')}
            </button>
          </div>
        </div>
        {isEdit && attachmentUrlsToKeep.length > 0 && (
          <div>
            <label className={labelClass}>{t('report.currentAttachments')}</label>
            <ul className="mt-2 space-y-2">
              {attachmentUrlsToKeep.map((url, idx) => (
                <li key={url + idx} className="flex items-center gap-2 flex-wrap">
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-shayek-primary dark:text-shayek-red-light hover:underline break-all">
                    {(attachmentFilenamesToKeep[idx] || url).length > 50 ? (attachmentFilenamesToKeep[idx] || url).slice(0, 47) + '...' : (attachmentFilenamesToKeep[idx] || url)}
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      setAttachmentUrlsToKeep((prev) => prev.filter((_, i) => i !== idx));
                      setAttachmentFilenamesToKeep((prev) => prev.filter((_, i) => i !== idx));
                    }}
                    className="px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                  >
                    {t('common.remove')}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div>
          <label className={labelClass}>{t('report.attachment')} ({t('common.optional')})</label>
          {isEdit && <p className="text-xs text-shayek-light-600 dark:text-shayek-dark-500 mb-1">{t('report.addNewAttachmentsHint', 'Add new files below; they will be saved with the report.')}</p>}
          <FileUpload
            multiple
            onFileSelect={(files) => { setSelectedFiles(Array.isArray(files) ? files : []); setFileError(''); }}
            selectedFiles={selectedFiles}
            error={fileError}
          />
        </div>
      </div>

      {/* Verification */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>{t('report.initialVerification')}</label>
          <select value={initialVerification} onChange={e => setInitialVerification(e.target.value)} className={inputClass}>
            <option value="">—</option>
            <option value="yes">{t('report.initialVerificationOptions.yes')}</option>
            <option value="partial">{t('report.initialVerificationOptions.partial')}</option>
            <option value="no">{t('report.initialVerificationOptions.no')}</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>{t('report.reliabilityLevel')}</label>
          <select value={reliabilityLevel} onChange={e => setReliabilityLevel(e.target.value)} className={inputClass}>
            <option value="">—</option>
            <option value="high">{t('report.reliabilityLevelOptions.high')}</option>
            <option value="medium">{t('report.reliabilityLevelOptions.medium')}</option>
            <option value="low">{t('report.reliabilityLevelOptions.low')}</option>
          </select>
        </div>
      </div>
      <div>
        <label className={labelClass}>{t('report.verificationNotes')}</label>
        <textarea value={verificationNotes} onChange={e => setVerificationNotes(e.target.value)} className={`${inputClass} h-24`} maxLength={2000} />
      </div>

      {/* Safety risks */}
      <div>
        <label className={labelClass}>{t('report.safetyRisks')}</label>
        <div className="flex flex-wrap gap-3">
          {SAFETY_RISK_KEYS.map(key => (
            <label key={key} className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={safetyRisks.includes(key)} onChange={() => setSafetyRisks(toggleArray(safetyRisks, key))} className="rounded border-shayek-light-300 text-shayek-primary focus:ring-shayek-primary" />
              <span className="text-sm">{t(`report.safetyRisksOptions.${key}`)}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Legal classification */}
      <div>
        <label className={labelClass}>{t('report.legalClassification')}</label>
        <div className="flex flex-wrap gap-3">
          {LEGAL_KEYS.map(key => (
            <label key={key} className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={legalClassification.includes(key)} onChange={() => setLegalClassification(toggleArray(legalClassification, key))} className="rounded border-shayek-light-300 text-shayek-primary focus:ring-shayek-primary" />
              <span className="text-sm">{t(`report.legalClassificationOptions.${key}`)}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Reporter notes */}
      <div>
        <label className={labelClass}>{t('report.reporterNotes')}</label>
        <textarea value={reporterNotes} onChange={e => setReporterNotes(e.target.value)} className={`${inputClass} h-24`} placeholder={t('report.reporterNotesPlaceholder')} maxLength={2000} />
        <p className="mt-1 text-xs text-gray-500 dark:text-shayek-dark-500">{reporterNotes.length}/2000</p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>
      )}

      <div className="flex gap-4">
        <button type="submit" disabled={loading} className="flex-1 py-3 bg-shayek-primary dark:bg-shayek-red-light text-white rounded-lg font-bold disabled:opacity-50 hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark transition-colors">
          {loading ? t('common.loading') : (isEdit ? t('report.saveReport') : t('report.submitReport'))}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-6 py-3 bg-gray-200 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-300 rounded-lg hover:bg-gray-300 dark:hover:bg-shayek-dark-300 transition-colors">
            {t('common.cancel')}
          </button>
        )}
      </div>
    </form>
  );
};

export default ReportForm;
